// #1391: Web3Auth facade。@web3auth/* (~1.7MB) は opt-in の Web3 mode でしか
// 使われないのに main bundle に静的に入っていたため、実体を web3authImpl.ts に
// 分離し、ここから dynamic import で遅延ロードする。
//
// - 既存の呼び出し側 (main.tsx / Login.tsx / useStore.ts / ethers.ts) は
//   import 元を変えずにそのまま動く (export 名・シグネチャ互換)
// - 実体が未ロードの間、getWeb3Auth() / isWeb3AuthConnected() は
//   「未初期化」を意味する null / false を返す (従来の未 init 時と同じ挙動)
import type { Web3Auth } from "@web3auth/modal";
import { clearEthersCache } from "./ethers";

const clientId = import.meta.env.VITE_WEB3AUTH_CLIENT_ID || "";

/** True when VITE_WEB3AUTH_CLIENT_ID is set */
export const isWeb3AuthEnabled = () => !!clientId;

type Impl = typeof import("./web3authImpl");

let impl: Impl | null = null;
let implPromise: Promise<Impl> | null = null;

const loadImpl = (): Promise<Impl> => {
  implPromise ??= import("./web3authImpl").then((m) => {
    impl = m;
    return m;
  });
  return implPromise;
};

/**
 * Lazily initialise Web3Auth.  Safe to call multiple times.
 * Returns null when client-id is missing.
 */
export const initWeb3Auth = async (): Promise<Web3Auth | null> => {
  if (!clientId) return null;
  return (await loadImpl()).initWeb3Auth();
};

/** Get the singleton (may be null if not initialised). */
export const getWeb3Auth = (): Web3Auth | null => impl?.getWeb3Auth() ?? null;

/**
 * Open the Web3Auth login modal.
 * Returns user profile + EVM wallet address.
 */
export const loginWithWeb3Auth = async (): Promise<
  Awaited<ReturnType<Impl["loginWithWeb3Auth"]>>
> => {
  if (!clientId) throw new Error("Web3Auth is not configured");
  return (await loadImpl()).loginWithWeb3Auth();
};

/** Disconnect from Web3Auth and clear ethers cache. */
export const logoutWeb3Auth = async (): Promise<void> => {
  // 実体が未ロード = このタブで Web3Auth session は張られていないので、
  // logout のためだけに 1.7MB をロードしない。cache clear だけで十分。
  if (!impl) {
    clearEthersCache();
    return;
  }
  return impl.logoutWeb3Auth();
};

/** Check if a session is currently active. */
export const isWeb3AuthConnected = () => impl?.isWeb3AuthConnected() ?? false;
